import { useState, useEffect } from "react"
import { useSearchParams } from "react-router-dom"
import { OpportunityHeader } from "@/components/opportunities/OpportunityHeader"
import { ActiveFilters } from "@/components/opportunities/ActiveFilters"
import { CookieConsent } from "@/components/CookieConsent"
import { OpportunitiesMetaTags } from "@/components/opportunities/OpportunitiesMetaTags"
import { OpportunitiesFilters } from "@/components/opportunities/OpportunitiesFilters"
import { OpportunitiesResultsNew } from "@/components/opportunities/OpportunitiesResultsNew"
import SEO from '@/components/SEO';
import { useOpportunitiesApi } from "@/hooks/useOpportunitiesApi"
import { useInView } from "react-intersection-observer"

export default function OpportunitiesPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const searchQuery = searchParams.get('q')
  
  const [focusArea, setFocusArea] = useState<string[]>([])
  const [beneficiaryType, setBeneficiaryType] = useState<string[]>([])
  const [format, setFormat] = useState<string[]>([])
  const [location, setLocation] = useState<string | undefined>(undefined)
  
  // Create ref for intersection observer
  const { ref, inView } = useInView({
    threshold: 0.1,
    rootMargin: "0px 0px 200px 0px" // Load more when user is 200px from bottom
  })

  // Effect to handle search query from URL
  useEffect(() => {
    if (searchQuery) {
      // If there's a search query, we could implement parsing logic here
      // For now, we'll just use it directly in the API call
    }
  }, [searchQuery]);

  const { 
    data, 
    fetchNextPage, 
    hasNextPage, 
    isFetchingNextPage 
  } = useOpportunitiesApi({
    focusArea,
    beneficiaryType,
    format,
    location,
    searchQuery
  })

  // Effect to fetch next page when user scrolls to bottom
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [inView, fetchNextPage, hasNextPage, isFetchingNextPage])

  // Flatten all opportunities from paginated results
  const opportunities = data?.pages.flatMap(page => page.data) || []
  
  // Get total count from the latest pagination data
  const totalCount = data?.pages[0]?.pagination.total || 0

  // Extract unique values for filters
  const uniqueFocusAreas = [
    ...new Set(opportunities.map((opp) => opp.focus_area)),
  ]
  const uniqueBeneficiaryTypes = [
    ...new Set(
      opportunities.flatMap((opp) => opp.beneficiary_types)
    ),
  ]
  const uniqueFormats = [
    ...new Set(opportunities.map((opp) => opp.format)),
  ]
  const uniqueLocations = [
    ...new Set(opportunities.map((opp) => opp.location)),
  ]

  const removeFilter = (filterType: string, value: string) => {
    switch (filterType) {
      case 'focusArea':
        setFocusArea(prev => prev.filter(item => item !== value));
        break;
      case 'beneficiaryType':
        setBeneficiaryType(prev => prev.filter(item => item !== value));
        break;
      case 'format':
        setFormat(prev => prev.filter(item => item !== value));
        break;
      default:
        break;
    }
  };

  const clearAllFilters = () => {
    setFocusArea([]);
    setBeneficiaryType([]);
    setFormat([]);
    setLocation(undefined);
  };

  return (
    <>
      <SEO 
        title="Volunteer Opportunities" 
        description="Discover meaningful volunteer opportunities and make a real impact in your community. Browse our curated list of CSR initiatives and events."
      />
      <OpportunitiesMetaTags searchQuery={searchQuery} />
      
      <OpportunityHeader 
        onFiltersChange={(filters) => {
          setFocusArea(filters.focus_areas || []);
          setBeneficiaryType(filters.beneficiary_types || []);
          setFormat(filters.format ? [filters.format] : []);
          setLocation(filters.location);
        }} 
      />

      <div className="container mx-auto py-8 px-4">
        <ActiveFilters
          focusArea={focusArea}
          beneficiaryType={beneficiaryType}
          format={format}
          location={location}
          onRemove={removeFilter}
          onClearAll={clearAllFilters}
        />

        <div className="flex flex-col lg:flex-row gap-6 mt-6">
          <div className="w-full lg:w-1/4">
            <OpportunitiesFilters
              focusArea={focusArea}
              setFocusArea={setFocusArea}
              beneficiaryType={beneficiaryType}
              setBeneficiaryType={setBeneficiaryType}
              format={format}
              setFormat={setFormat}
              location={location}
              setLocation={setLocation}
              availableFocusAreas={uniqueFocusAreas}
              availableBeneficiaryTypes={uniqueBeneficiaryTypes}
              availableFormats={uniqueFormats}
              availableLocations={uniqueLocations}
            />
          </div>
          <div className="w-full lg:w-3/4">
            <OpportunitiesResultsNew opportunities={opportunities} totalCount={totalCount} />
            
            {/* Infinite scroll loading indicator */}
            <div ref={ref} className="flex justify-center mt-8 h-16">
              {isFetchingNextPage && (
                <div className="flex items-center space-x-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-primary"></div>
                  <p className="text-sm text-gray-500">Loading more opportunities...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <CookieConsent />
    </>
  )
}
